import { Badge, FormStatus, Tooltip } from '@dnb/eufemia';
import { HelpProps } from '@dnb/eufemia/components/help-button/HelpButtonInline';
import { Field, Form, Value, ValueBlock } from '@dnb/eufemia/extensions/forms';
import { ApiError } from '@portals/shared-frontend/utils/ApiError';
import { type JSX, useCallback, useId, useState } from 'react';

import { updateClient } from '@/api/app';
import Toast from '@/components/Toast';

import { CrqField } from '../../../CrqField';

export type DetailsFormData = {
  crq?: string;
  description: string;
  name: string;
};

export const crqHelp: HelpProps = {
  title: 'ServiceNow Identifier (BA/BSN)',
  content:
    'A ServiceNow change request (CRQ) in the "implement" state is required for updating a client in the live environment. Enter the ID for the relevant CRQ.',
};

export const crqLabelDescription = 'All changes to clients in live environment require a valid CRQ.';

export const busyEditingTooltip = (
  <Tooltip size="large" style={{ maxWidth: '300px' }}>
    Finish editing the other section before making changes here.
  </Tooltip>
);

function EnvironmentBadge({ liveMode }: { liveMode: boolean }) {
  return liveMode ? <Badge content="LIVE" status="positive" /> : <Badge content="TEST" />;
}

type Props = {
  appId: string;
  canEdit: boolean;
  clientId: string;
  description: string;
  editDisabled: boolean;
  liveMode: boolean;
  name: string;
  onEdit: () => void;
  onEditEnd: () => void;
  onSaved: () => Promise<unknown>;
  scopes: string[];
};

export default function DetailsSection({
  appId,
  canEdit,
  clientId,
  description,
  editDisabled,
  liveMode,
  name,
  onEdit,
  onEditEnd,
  onSaved,
  scopes,
}: Props): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const formId = `update-client-details-${useId()}`;

  const handleDone = useCallback(async () => {
    const { data } = Form.getData<DetailsFormData>(formId);

    // The CRQ field is not `required` (that would trap Cancel, whose restored
    // value is always empty), so guard it here. Throwing keeps the section open.
    if (liveMode && !data.crq) {
      setErrorMessage('A ServiceNow CRQ is required to change a client in the live environment.');
      throw new Error('Missing CRQ');
    }

    try {
      setErrorMessage(undefined);
      await updateClient(
        appId,
        clientId,
        { description: data.description, name: data.name, scopes },
        liveMode ? data.crq : undefined,
      );
      await onSaved();
      onEditEnd();
      setSuccessMessage('Client details updated');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      if (ApiError.isApiError(error)) {
        setErrorMessage(error.body.message);
      } else {
        setErrorMessage('An unexpected error occurred');
      }
      // Rejecting is what keeps the section in edit mode with the input intact,
      // so the user can correct it and retry.
      throw error;
    }
  }, [appId, clientId, formId, liveMode, onEditEnd, onSaved, scopes]);

  return (
    <Form.Handler<DetailsFormData>
      defaultData={{ crq: undefined, description, name }}
      id={formId}
      locale="en-GB"
      translations={{ 'en-GB': { SectionEditContainer: { doneButton: 'Save' } } }}
    >
      {successMessage && <Toast message={successMessage} position="bottom" variant="success" />}
      <Form.Section containerMode="view" disableEditing={!canEdit}>
        <Form.Section.ViewContainer>
          <Value.SummaryList layout="horizontal">
            <Value.String label="Client name" path="/name" showEmpty />
            <Value.String label="Client description" path="/description" showEmpty />
            <Value.Composition label="Environment">
              <EnvironmentBadge liveMode={liveMode} />
            </Value.Composition>
          </Value.SummaryList>
          {canEdit && (
            <Form.Section.Toolbar onEdit={onEdit}>
              <Form.Section.ViewContainer.EditButton
                disabled={editDisabled}
                tooltip={editDisabled ? busyEditingTooltip : undefined}
              />
            </Form.Section.Toolbar>
          )}
        </Form.Section.ViewContainer>
        <Form.Section.EditContainer title="Edit details">
          <Field.String
            help={{
              content: 'This will help to identify the client.',
            }}
            label="Client name"
            maxLength={255}
            minLength={3}
            path="/name"
            placeholder="Client name"
            required
            size="medium"
            width="stretch"
          />
          <Field.String
            label="Client description"
            maxLength={500}
            minLength={3}
            path="/description"
            placeholder="Client description"
            required
            size="medium"
            top="small"
            width="stretch"
          />
          <ValueBlock label="Environment">
            <EnvironmentBadge liveMode={liveMode} />
          </ValueBlock>
          {liveMode && (
            <CrqField customHelp={crqHelp} labelDescription={crqLabelDescription} path="/crq" required={false} />
          )}
          <Form.Visibility animate visible={!!errorMessage}>
            <FormStatus state="error" stretch text={errorMessage} top="small" />
          </Form.Visibility>
          {/* Own toolbar so Cancel skips the "discard your changes?" confirm. */}
          <Form.Section.Toolbar onCancel={onEditEnd} onDone={handleDone}>
            <Form.Section.EditContainer.DoneButton />
            <Form.Section.EditContainer.CancelButton showConfirmDialog={false} />
          </Form.Section.Toolbar>
        </Form.Section.EditContainer>
      </Form.Section>
    </Form.Handler>
  );
}
